import { useQuery } from "@tanstack/react-query";
import { createContext, type ReactNode, useContext } from "react";
import { z } from "zod";
import { httpClient } from "@/api/client";
import { getSelskapsportalUserSelskaperOptions } from "@/api/generated/@tanstack/react-query.gen";
import usePersistantState, {
  getPersistantState,
} from "@/hooks/usePersistantState";

export const selskapSchema = z.object({
  organisasjonsnummer: z.number(),
  navn: z.string(),
});

export type SelskapSchema = z.infer<typeof selskapSchema>;

type SelskapContextType = {
  isLoadingSelskaper: boolean;
  isErrorLoadingSelskaper: boolean;
  selskaper: SelskapSchema[];
  active: SelskapSchema | null;
  setActiveSelskap: (selskap: SelskapSchema | null) => void;
};

const SelskapContext = createContext<SelskapContextType | undefined>(undefined);

export const ActiveSelskapStorageKey = "activeSelskap";

export const SelskapContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [activeSelskap, setActiveSelskap] =
    usePersistantState<SelskapSchema | null>(
      ActiveSelskapStorageKey,
      selskapSchema,
      getPersistantState(ActiveSelskapStorageKey, localStorage),
      localStorage,
    );
  const query = useQuery({
    ...getSelskapsportalUserSelskaperOptions({
      client: httpClient, // Use cleanHttpClient to avoid redirect loops on 401
    }),
  });

  const hovedenheter = query.data?.selskaper || [];
  const allSelskaper: SelskapSchema[] = hovedenheter.map((he) => ({
    organisasjonsnummer: he.organisasjonsnummer as number,
    navn: he.navn,
  }));

  return (
    <SelskapContext.Provider
      value={{
        isLoadingSelskaper: query.isLoading,
        isErrorLoadingSelskaper: query.isError,
        active: activeSelskap,
        setActiveSelskap: setActiveSelskap,
        selskaper: allSelskaper,
      }}
    >
      {children}
    </SelskapContext.Provider>
  );
};

export function useSelskaper() {
  const context = useContext(SelskapContext);
  if (!context) {
    throw new Error(
      "useSelskaper must be used within an SelskapContextProvider",
    );
  }
  return context;
}
